import { useState, useEffect, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/auth';
import { toast } from '@/lib/toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Lazy accessor for posthog to avoid pulling 50KB+ into the initial bundle
const getPosthog = () => import('posthog-js').then(m => m.default);

// Shared device authorization (QR login)
const DEVICE_AUTHORIZED_TEAM_KEY = 'team_v_authorized_team';

// Team selection persistence
const CURRENT_TEAM_KEY = 'team-v-does-current-team';

// Explicit column list — keep in sync with dbRowToProfile.
// Used to avoid select('*') exposing future sensitive columns (SEC-M7).
const PROFILE_COLUMNS =
  'id, member_id, user_id, email, display_name, avatar_url, created_at, updated_at, ' +
  'preferred_language, is_findable, default_team_id, motivation_reminder_enabled, ' +
  'motivation_reminder_time, timezone, last_seen_at';

const dbRowToProfile = (row: any): Profile => ({
  id: row.id,
  memberId: row.member_id,
  userId: row.user_id,
  email: row.email,
  displayName: row.display_name,
  avatarUrl: row.avatar_url,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
  preferredLanguage: (row.preferred_language as 'nl' | 'en') || 'nl',
  isFindable: row.is_findable || false,
  defaultTeamId: row.default_team_id || null,
  motivationReminderEnabled: row.motivation_reminder_enabled || false,
  motivationReminderTime: row.motivation_reminder_time || '09:00',
  timezone: row.timezone || 'UTC',
  lastSeenAt: row.last_seen_at ? new Date(row.last_seen_at) : null,
});

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const queryClient = useQueryClient();

  // Debug helpers
  const isAuthDebugEnabled = useCallback(() => {
    try {
      return sessionStorage.getItem('auth_debug_enabled') === '1';
    } catch {
      return false;
    }
  }, []);

  const logDebug = useCallback((message: string, data?: unknown) => {
    if (!isAuthDebugEnabled()) return;
    if (data !== undefined) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }, [isAuthDebugEnabled]);

  // 1. Manage User/Session State via Supabase Auth
  useEffect(() => {
    let isMounted = true;

    // Listener for ONGOING auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (!isMounted) return;
        logDebug('[useAuth] Auth state change:', event);
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        setSessionLoading(false);
      }
    );

    // INITIAL load
    (async () => {
      try {
        logDebug('[useAuth] Checking existing session...');
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (error) {
          console.error('[useAuth] Error getting session:', error);
        }

        setSession(initialSession);
        setUser(initialSession?.user ?? null);
      } catch (err) {
        console.error('[useAuth] Session check failed:', err);
      } finally {
        if (isMounted) setSessionLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [logDebug]);

  // PostHog Identification (Debounced to prevent redundant calls)
  const lastIdentifiedId = useRef<string | null>(null);

  useEffect(() => {
    if (user && user.id !== lastIdentifiedId.current) {
      getPosthog().then(ph => ph.identify(user.id, {
        email: user.email,
        name: user.user_metadata?.full_name || user.email?.split('@')[0]
      }));
      lastIdentifiedId.current = user.id;
    } else if (!sessionLoading && !user && lastIdentifiedId.current !== null) {
      getPosthog().then(ph => ph.reset());
      lastIdentifiedId.current = null;
    } else if (!sessionLoading && !user) {
      getPosthog().then(ph => ph.reset());
    }
  }, [user, sessionLoading]);

  // 2. Fetch/Create Profile via React Query
  const { data: profile = null, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    enabled: !!user?.id,
    staleTime: Infinity, // Profile data updates rarely, invalidate on mutation
    queryFn: async () => {
      if (!user) return null;

      // Try fetch
      const { data, error } = await supabase
        .from('profiles')
        .select(PROFILE_COLUMNS)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        return null; // Don't throw to avoid crashing app, rely on null profile handling
      }

      if (data) {
        return dbRowToProfile(data);
      }

      // If not found, create
      const { data: newData, error: createError } = await supabase
        .from('profiles')
        .insert({
          user_id: user.id,
          email: user.email,
          display_name: user.user_metadata?.full_name || user.email?.split('@')[0] || null,
          avatar_url: user.user_metadata?.avatar_url || null,
          is_findable: false,
        })
        .select(PROFILE_COLUMNS)
        .maybeSingle();

      if (createError) {
        // Handle race condition where it might have been created in parallel
        if (createError.code === '23505') {
          const { data: retryData } = await supabase
            .from('profiles')
            .select(PROFILE_COLUMNS)
            .eq('user_id', user.id)
            .single();
          if (retryData) return dbRowToProfile(retryData);
        }
        console.error('Error creating profile:', createError);
        return null;
      }

      return newData ? dbRowToProfile(newData) : null;
    }
  });

  // 3. Mutations
  const updateProfileMutation = useMutation({
    mutationFn: async (updates: Partial<Pick<Profile, 'displayName' | 'avatarUrl' | 'isFindable' | 'defaultTeamId' | 'motivationReminderEnabled' | 'motivationReminderTime' | 'timezone' | 'lastSeenAt'>>) => {
      if (!user) throw new Error('Not authenticated');

      const dbUpdates: Record<string, any> = {};
      if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
      if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;
      if (updates.isFindable !== undefined) dbUpdates.is_findable = updates.isFindable;
      if (updates.defaultTeamId !== undefined) dbUpdates.default_team_id = updates.defaultTeamId;
      if (updates.motivationReminderEnabled !== undefined) dbUpdates.motivation_reminder_enabled = updates.motivationReminderEnabled;
      if (updates.motivationReminderTime !== undefined) dbUpdates.motivation_reminder_time = updates.motivationReminderTime;
      if (updates.timezone !== undefined) dbUpdates.timezone = updates.timezone;
      if (updates.lastSeenAt !== undefined) dbUpdates.last_seen_at = updates.lastSeenAt?.toISOString();

      const { error } = await supabase
        .from('profiles')
        .update(dbUpdates as any)
        .eq('user_id', user.id);

      if (error) throw error;
      return updates;
    },
    onSuccess: (updates) => {
      queryClient.setQueryData<Profile | null>(['profile', user?.id], (old) => {
        if (!old) return null;
        return { ...old, ...updates };
      });

      // Don't show toast for automatic background updates
      const keys = Object.keys(updates).filter(k => updates[k as keyof typeof updates] !== undefined);
      const isBackgroundUpdate = keys.length > 0 && keys.every(k => k === 'lastSeenAt' || k === 'timezone');

      if (!isBackgroundUpdate) {
        toast.success('Profiel bijgewerkt', { id: 'auth_profile_updated', channel: 'auth' });
      }
    },
    onError: () => {
      toast.error('Profiel bijwerken mislukt', { id: 'auth_profile_update_error', channel: 'auth' });
    }
  });

  const updateProfile = useCallback(async (updates: Partial<Pick<Profile, 'displayName' | 'avatarUrl' | 'isFindable' | 'defaultTeamId' | 'motivationReminderEnabled' | 'motivationReminderTime' | 'timezone' | 'lastSeenAt'>>) => {
    try {
      // Use mutateAsync directly from the mutation object to avoid dependency on the changing mutation status
      await updateProfileMutation.mutateAsync(updates);
      return { error: null };
    } catch (e) {
      return { error: e as Error };
    }
  }, [updateProfileMutation.mutateAsync]);


  // Sign in with email/password
  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        toast.error(error.message, { id: 'auth_signin_error', channel: 'auth' });
      }
      return { error };
    }
    toast.success('Succesvol ingelogd!', { id: 'auth_signin', channel: 'auth' });
    return { error: null };
  }, []);

  // Sign up with email/password
  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    if (error) {
      if (error.message.includes('already registered')) {
        toast.error('Dit e-mailadres is al geregistreerd', { channel: 'auth' });
      } else {
        toast.error(error.message, { channel: 'auth' });
      }
      return { error };
    }

    toast.success('Account aangemaakt! Je bent nu ingelogd.', { id: 'auth_signin', channel: 'auth' });
    return { error: null };
  }, []);

  // Sign in with Google
  const signInWithGoogle = useCallback(async () => {
    // Robust detection: Check for Capacitor bridge directly
    const isNative = !!(window as any).Capacitor?.isNativePlatform();

    // Fallback: If hostname is NOT a standard web domain, we're likely in the app shell
    const isProbablyInApp = isNative || window.location.hostname === 'localhost' || window.location.protocol === 'app:';

    // For native apps, use an https:// URL that Supabase allows,
    // which then redirects to app.clando:// via the /app-redirect page
    const redirectTo = isProbablyInApp
      ? 'https://clando.app/app-redirect'
      : `${window.location.origin}`;

    console.log('[Auth] Google Sign-In | isNative:', isNative, '| redirectTo:', redirectTo);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        skipBrowserRedirect: false,
      },
    });

    if (error) {
      console.error('[Auth] Google login failed:', error);
      toast.error(error.message || 'Google login mislukt');
      return { error };
    }
    return { error: null };
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    let signOutError: Error | null = null;
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        signOutError = error;
      }
    } catch (e) {
      signOutError = e as Error;
    }

    // Clear any device/team locks that could cause cross-session confusion.
    try {
      localStorage.removeItem(DEVICE_AUTHORIZED_TEAM_KEY);
      localStorage.removeItem('team_v_authorized_device_id');
      localStorage.removeItem(CURRENT_TEAM_KEY);
      localStorage.removeItem('team-v-does-default-team');
    } catch {
      // ignore
    }

    // Clear queries
    queryClient.clear();
    setUser(null);
    setSession(null);

    if (signOutError) {
      console.error('Sign out failed:', signOutError);
      toast.error(signOutError.message || 'Uitloggen mislukt', { id: 'auth_signout_error', channel: 'auth' });
      return { error: signOutError };
    }

    toast.success('Uitgelogd', { id: 'auth_signout', channel: 'auth' });
    return { error: null };
  }, [queryClient]);

  // Join a team
  const joinTeam = useCallback(async (teamId: string, asAdmin = false) => {
    if (!user) return { error: new Error('Not authenticated') };

    // Explicit column allowlist on read-back: keeps future sensitive
    // team_members columns (invite tokens, billing seat IDs) off the client
    // unless we add them here deliberately. See SEC-M7.
    const { error } = await supabase
      .from('team_members')
      .insert({
        team_id: teamId,
        user_id: user.id,
        role: asAdmin ? 'admin' : 'member',
      })
      .select('id')
      .maybeSingle();

    if (error) {
      if (error.code === '23505') {
        // Already a member
        return { error: null };
      }
      console.error('Error joining team:', error);
      return { error };
    }

    return { error: null };
  }, [user]);

  // Leave a team (with last admin protection)
  const leaveTeam = useCallback(async (teamId: string) => {
    if (!user) return { error: new Error('Not authenticated') };

    // Check if user is an admin
    const { data: membership, error: membershipError } = await supabase
      .from('team_members')
      .select('role')
      .eq('team_id', teamId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (membershipError) {
      console.error('Error fetching membership role:', membershipError);
      return { error: membershipError };
    }

    if (membership?.role === 'admin') {
      // Count admins in this team
      const { data: admins, error: countError } = await supabase
        .from('team_members')
        .select('id')
        .eq('team_id', teamId)
        .eq('role', 'admin');

      if (countError) {
        console.error('Error counting admins:', countError);
        return { error: countError };
      }

      if (admins && admins.length <= 1) {
        toast.error('Je kunt het team niet verlaten als laatste admin. Maak eerst iemand anders admin.', { id: 'team_leave_error', channel: 'teams' });
        return { error: new Error('Cannot leave as last admin') };
      }
    }

    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error leaving team:', error);
      return { error };
    }

    return { error: null };
  }, [user]);

  // Promote a member to admin
  const promoteToAdmin = useCallback(async (teamId: string, targetUserId: string) => {
    if (!user) return { error: new Error('Not authenticated') };

    const { data, error } = await supabase
      .from('team_members')
      .update({ role: 'admin' })
      .eq('team_id', teamId)
      .eq('user_id', targetUserId)
      .select('id');

    if (error) {
      console.error('Error promoting member:', error);
      const isAuthz = (error as { code?: string }).code === '42501';
      toast.error(isAuthz ? 'Alleen admins kunnen rollen wijzigen' : 'Fout bij promoveren naar admin', { id: 'team_member_promoted_error', channel: 'teams' });
      return { error };
    }

    if (!data || data.length === 0) {
      toast.error('Alleen admins kunnen rollen wijzigen', { id: 'team_member_promoted_error', channel: 'teams' });
      return { error: new Error('Forbidden') };
    }

    toast.success('Lid gepromoveerd tot admin', { id: 'team_member_promoted', channel: 'teams' });
    return { error: null };
  }, [user]);

  // Demote an admin to member (with last admin protection)
  const demoteFromAdmin = useCallback(async (teamId: string, targetUserId: string) => {
    if (!user) return { error: new Error('Not authenticated') };

    // Count admins in this team
    const { data: admins, error: countError } = await supabase
      .from('team_members')
      .select('id')
      .eq('team_id', teamId)
      .eq('role', 'admin');

    if (countError) {
      console.error('Error counting admins:', countError);
      return { error: countError };
    }

    if (admins && admins.length <= 1) {
      toast.error('Er moet minstens één admin in het team blijven', { channel: 'auth' });
      return { error: new Error('Cannot demote last admin') };
    }

    const { data, error } = await supabase
      .from('team_members')
      .update({ role: 'member' })
      .eq('team_id', teamId)
      .eq('user_id', targetUserId)
      .select('id');

    if (error) {
      console.error('Error demoting admin:', error);
      const isAuthz = (error as { code?: string }).code === '42501';
      toast.error(isAuthz ? 'Alleen admins kunnen rollen wijzigen' : 'Fout bij degraderen van admin', { id: 'team_member_demoted_error', channel: 'teams' });
      return { error };
    }

    if (!data || data.length === 0) {
      toast.error('Alleen admins kunnen rollen wijzigen', { id: 'team_member_demoted_error', channel: 'teams' });
      return { error: new Error('Forbidden') };
    }

    toast.success('Admin gedegradeerd tot lid', { id: 'team_member_demoted', channel: 'teams' });
    return { error: null };
  }, [user]);

  // Remove a member from team (admin only, with last admin protection)
  const removeMemberFromTeam = useCallback(async (teamId: string, targetUserId: string) => {
    if (!user) return { error: new Error('Not authenticated') };

    // Check if target is an admin
    const { data: targetMember, error: targetError } = await supabase
      .from('team_members')
      .select('role')
      .eq('team_id', teamId)
      .eq('user_id', targetUserId)
      .maybeSingle();

    if (targetError) {
      console.error('Error checking target member:', targetError);
      return { error: targetError };
    }

    if (targetMember?.role === 'admin') {
      // Count admins in this team
      const { data: admins, error: countError } = await supabase
        .from('team_members')
        .select('id')
        .eq('team_id', teamId)
        .eq('role', 'admin');

      if (countError) {
        console.error('Error counting admins:', countError);
        return { error: countError };
      }

      if (admins && admins.length <= 1) {
        toast.error('De laatste admin kan niet worden verwijderd', { channel: 'auth' });
        return { error: new Error('Cannot remove last admin') };
      }
    }

    const { data, error } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', targetUserId)
      .select('id');

    if (error) {
      console.error('Error removing member:', error);
      const isAuthz = (error as { code?: string }).code === '42501';
      toast.error(isAuthz ? 'Alleen admins kunnen leden verwijderen' : 'Fout bij verwijderen van lid', { id: 'team_member_removed_error', channel: 'teams' });
      return { error };
    }

    if (!data || data.length === 0) {
      toast.error('Alleen admins kunnen leden verwijderen', { id: 'team_member_removed_error', channel: 'teams' });
      return { error: new Error('Forbidden') };
    }

    toast.success('Lid verwijderd uit team', { id: 'team_member_removed', channel: 'teams' });
    return { error: null };
  }, [user]);

  return {
    user,
    session,
    profile,
    loading: sessionLoading || (!!user && profileLoading),
    isAuthenticated: !!user,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    updateProfile,
    joinTeam,
    leaveTeam,
    promoteToAdmin,
    demoteFromAdmin,
    removeMemberFromTeam,
  };
}
